import type {
  BackofficeMessageBusDeadLetterActionRequest,
  BackofficeMessageBusDeadLetterActionResponse,
  BackofficeMessageBusMessageSource,
  BackofficeMessageBusOverview,
  BackofficeMessageBusSubscriberMessagesResponse,
  BackofficeMessageBusSubscriberDetail,
  BackofficeMessageBusTopicDetail,
  BackofficeMessageBusTopicsResponse,
} from './types';
import { backofficeFetch } from '@/services/backoffice-api';

const CACHE_TTL_MS = 60_000;
const CACHE_KEY_PREFIX = 'backoffice:messagebus:';

interface CacheEntry<T> {
  expiresAt: number;
  response: T;
}

function cacheKey(path: string): string {
  return `${CACHE_KEY_PREFIX}${path}`;
}

function readCached<T>(path: string): T | null {
  try {
    const serialized = sessionStorage.getItem(cacheKey(path));
    if (!serialized) {
      return null;
    }

    const entry = JSON.parse(serialized) as CacheEntry<T>;
    if (entry.expiresAt <= Date.now()) {
      sessionStorage.removeItem(cacheKey(path));
      return null;
    }

    return entry.response;
  } catch {
    return null;
  }
}

function writeCached<T>(path: string, response: T): void {
  try {
    sessionStorage.setItem(
      cacheKey(path),
      JSON.stringify({
        expiresAt: Date.now() + CACHE_TTL_MS,
        response,
      } satisfies CacheEntry<T>)
    );
  } catch {
    // no-op
  }
}

export function clearMessageBusCache(): void {
  try {
    const keys = [];
    for (let index = 0; index < sessionStorage.length; index += 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(CACHE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      sessionStorage.removeItem(key);
    }
  } catch {
    // no-op
  }
}

async function fetchJson<T>(path: string, signal?: AbortSignal, forceRefresh = false): Promise<T> {
  if (!forceRefresh) {
    const cached = readCached<T>(path);
    if (cached) {
      return cached;
    }
  }

  const response = await backofficeFetch(path, {
    headers: { Accept: 'application/json' },
    ...(signal ? { signal } : {}),
    ...(forceRefresh ? { cache: 'no-store' } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to load message bus data (${response.status})`);
  }

  const payload = (await response.json()) as T;
  writeCached(path, payload);
  return payload;
}

async function mutateJson<TResponse>(
  path: string,
  body: unknown,
  signal?: AbortSignal
): Promise<TResponse> {
  const response = await backofficeFetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new Error(`Failed to update message bus data (${response.status})`);
  }

  return (await response.json()) as TResponse;
}

export function fetchMessageBusOverview(
  signal?: AbortSignal,
  forceRefresh = false
): Promise<BackofficeMessageBusOverview> {
  return fetchJson('/messagebus', signal, forceRefresh);
}

export function fetchMessageBusTopics(
  signal?: AbortSignal,
  forceRefresh = false
): Promise<BackofficeMessageBusTopicsResponse> {
  return fetchJson('/messagebus/topics', signal, forceRefresh);
}

export function fetchMessageBusTopic(
  topicName: string,
  signal?: AbortSignal,
  forceRefresh = false
): Promise<BackofficeMessageBusTopicDetail> {
  return fetchJson(`/messagebus/topics/${encodeURIComponent(topicName)}`, signal, forceRefresh);
}

export function fetchMessageBusSubscriber(
  topicName: string,
  subscriberName: string,
  signal?: AbortSignal,
  forceRefresh = false
): Promise<BackofficeMessageBusSubscriberDetail> {
  return fetchJson(
    `/messagebus/topics/${encodeURIComponent(topicName)}/subscribers/${encodeURIComponent(subscriberName)}`,
    signal,
    forceRefresh
  );
}

export function fetchMessageBusSubscriberMessages(
  topicName: string,
  subscriberName: string,
  source: BackofficeMessageBusMessageSource,
  signal?: AbortSignal,
  forceRefresh = false
): Promise<BackofficeMessageBusSubscriberMessagesResponse> {
  return fetchJson(
    `/messagebus/topics/${encodeURIComponent(topicName)}/subscribers/${encodeURIComponent(subscriberName)}/messages?source=${encodeURIComponent(source)}`,
    signal,
    forceRefresh
  );
}

export function manageMessageBusDeadLetter(
  topicName: string,
  subscriberName: string,
  request: BackofficeMessageBusDeadLetterActionRequest,
  signal?: AbortSignal
): Promise<BackofficeMessageBusDeadLetterActionResponse> {
  return mutateJson(
    `/messagebus/topics/${encodeURIComponent(topicName)}/subscribers/${encodeURIComponent(subscriberName)}/dead-letter/actions`,
    request,
    signal
  );
}
